import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Settings, ShieldCheck, Loader2, AlertCircle, Mail, Lock, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import axios from "axios";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function Maintenance({ message }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const bypassKey = searchParams.get("key") || "";

  // Bypass states — ONLY active when URL has ?key= parameter
  const [bypassing, setBypassing] = useState(false);
  const [bypassError, setBypassError] = useState(null);
  const [bypassDone, setBypassDone] = useState(false);

  // Login states (only shown after VALID bypass with ?key= in URL)
  const [loginStep, setLoginStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [otp, setOtp] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [devOtp, setDevOtp] = useState("");

  const handleBypass = useCallback(async (token) => {
    setBypassing(true);
    setBypassError(null);
    setBypassDone(false);
    try {
      const res = await axios.post(`${API}/system/bypass`, { token }, { withCredentials: true });
      if (res.data.valid) {
        setBypassDone(true);
      }
    } catch (err) {
      setBypassError(err.response?.data?.detail || t("maintenance.tokenInvalid"));
      setBypassDone(false);
    } finally {
      setBypassing(false);
    }
  }, [t]);

  // Auto-validate bypass key from URL — ONLY when ?key= is present
  useEffect(() => {
    if (bypassKey && !bypassDone && !bypassing) {
      handleBypass(bypassKey);
    }
    // Reset bypass state when URL has no key
    if (!bypassKey) {
      setBypassDone(false);
      setBypassError(null);
      setLoginStep("credentials");
      setEmail("");
      setPassword("");
      setOtp("");
      setLoginError("");
    }
  }, [bypassDone, bypassKey, bypassing, handleBypass]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      if (res.data.requires_otp) {
        setTempToken(res.data.temp_token);
        // In dev/log mode, dev_otp is returned in response — auto-fill & display OTP
        if (res.data.dev_otp) {
          setOtp(res.data.dev_otp);
          setDevOtp(res.data.dev_otp);
        }
        setLoginStep("otp");
      }
    } catch (err) {
      setLoginError(err.response?.data?.detail || t("maintenance.loginFailed"));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");
    try {
      const res = await axios.post(`${API}/auth/verify-otp`, { temp_token: tempToken, otp }, { withCredentials: true });
      const user = res.data?.user || res.data;
      if (user?.role === "admin") {
        window.location.href = "/dashboard";
      } else {
        setLoginError(t("maintenance.adminOnly"));
        setLoginStep("credentials");
        setEmail("");
        setPassword("");
        setOtp("");
      }
    } catch (err) {
      setLoginError(err.response?.data?.detail || t("maintenance.otpFailed"));
    } finally {
      setLoginLoading(false);
    }
  };

  // Should we show the login form? ONLY when URL has valid key AND backend confirmed
  const showLoginForm = bypassKey && bypassDone;

  return (
    <div className="fixed inset-0 z-[200] bg-background flex items-center justify-center" data-testid="maintenance-page">
      <div className="text-center max-w-lg px-6">

        {/* Icon */}
        <div className="w-20 h-20 mx-auto mb-8 rounded-2xl bg-brand-primary/10 border border-brand-primary/20 flex items-center justify-center">
          <Settings className="w-10 h-10 text-brand-primary animate-[spin_8s_linear_infinite]" />
        </div>

        {/* Badge */}
        <div className="inline-block text-[10px] font-mono uppercase tracking-[.2em] px-4 py-1.5 bg-brand-primary/10 border border-brand-primary/20 rounded-full text-brand-primary mb-6">
          {t("maintenance.badge")}
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-4 tracking-tight">
          {t("maintenance.title")}
        </h1>

        {/* Message */}
        <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-8 max-w-md mx-auto">
          {message || t("maintenance.desc")}
        </p>

        {/* Bypass loading indicator */}
        {bypassKey && bypassing && (
          <div className="flex items-center justify-center gap-2 text-sm text-brand-primary mb-4" data-testid="bypass-loading">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>{t("maintenance.validating")}</span>
          </div>
        )}

        {/* Bypass error — only shown when URL has ?key= with invalid token */}
        {bypassKey && bypassError && (
          <div className="flex items-center justify-center gap-2 text-sm text-verdict-fake mb-4 bg-verdict-fake/10 border border-verdict-fake/20 rounded-lg px-4 py-3 max-w-sm mx-auto" data-testid="bypass-error">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{bypassError}</span>
          </div>
        )}

        {/* Admin Login Form — ONLY shown when URL has ?key= AND token is valid */}
        {showLoginForm && (
          <div className="mt-6 max-w-sm mx-auto" data-testid="bypass-login-section">
            <div className="flex items-center justify-center gap-2 mb-4">
              <ShieldCheck className="w-4 h-4 text-verdict-real" />
              <span className="text-sm font-medium text-verdict-real">{t("maintenance.bypassActive")}</span>
            </div>

            {loginError && (
              <div className="flex items-center gap-2 text-sm text-verdict-fake bg-verdict-fake/10 border border-verdict-fake/20 rounded-lg px-3 py-2 mb-4" data-testid="login-error">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                <span>{loginError}</span>
              </div>
            )}

            {loginStep === "credentials" && (
              <form onSubmit={handleLogin} className="space-y-3" data-testid="bypass-login-form">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("maintenance.emailPlaceholder")} required
                    className="w-full bg-secondary/50 border border-border rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary"
                    data-testid="bypass-email-input"
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("maintenance.passwordPlaceholder")} required
                    className="w-full bg-secondary/50 border border-border rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary"
                    data-testid="bypass-password-input"
                  />
                </div>
                <Button type="submit" disabled={loginLoading} className="w-full bg-brand-primary text-white rounded-lg h-10" data-testid="bypass-login-btn">
                  {loginLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t("maintenance.signIn")}
                </Button>
              </form>
            )}

            {loginStep === "otp" && (
              <form onSubmit={handleOtpVerify} className="space-y-3" data-testid="bypass-otp-form">
                <p className="text-xs text-muted-foreground mb-2">{t("maintenance.otpSent")}</p>
                {devOtp && (
                  <div className="text-xs font-mono bg-brand-primary/10 border border-brand-primary/30 rounded-lg px-3 py-2 text-brand-primary text-center tracking-[.2em]" data-testid="dev-otp-display">
                    OTP: <span className="font-bold text-sm">{devOtp}</span> <span className="text-[10px] opacity-60">(dev mode)</span>
                  </div>
                )}
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text" value={otp} onChange={(e) => setOtp(e.target.value)}
                  placeholder={t("maintenance.otpPlaceholder")} required maxLength={6}
                    className="w-full bg-secondary/50 border border-border rounded-lg pl-10 pr-4 py-2.5 text-sm text-center tracking-[.3em] font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
                    data-testid="bypass-otp-input"
                  />
                </div>
                <Button type="submit" disabled={loginLoading} className="w-full bg-brand-primary text-white rounded-lg h-10" data-testid="bypass-otp-btn">
                  {loginLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t("maintenance.verifyOtp")}
                </Button>
              </form>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground/40 font-mono uppercase tracking-[.15em] mt-8">
          <div className="w-8 h-px bg-muted-foreground/20" />
          Veriksa
          <div className="w-8 h-px bg-muted-foreground/20" />
        </div>
      </div>
    </div>
  );
}
